/**
 * Pull request reviews' data plane, in one injectable object.
 *
 * Every read and write the reviews page, the review sheet, the move sheet and
 * the Team queue's "Also today" fold make goes through `ReviewsApi`, so a test
 * hands a stub and nothing renders against a network. Each verb is one
 * existing route; nothing here invents a write the backend does not have.
 *
 *   listReviews        GET  /api/code-review/prs
 *   listDeskPrs        GET  /api/code-review/desks/:deskId/prs
 *   prReviews          GET  /api/code-review/pr-state/:id/reviews
 *   rerun              POST /api/code-review/pr-state/:id/rerun
 *   requestReview      POST /api/code-review/desks/:deskId/review-requests
 *   repoClaims         GET  /api/code-review/repo-claims
 *   moveRepoClaims     POST /api/code-review/repo-claims/move
 *   archiveWorkItem    POST /api/work-items/:id/archive
 *   harperRuns         GET  /api/workers/harper/engagements/:deskId/runs
 *   harperRun          GET  /api/workers/harper/runs/:runId
 *
 * A refusal carries the server's own sentence, so a sheet prints why a verb
 * did not land instead of a status code.
 */
import type {
  CodeReviewDeskPrsResponse,
  CodeReviewListResponse,
  CodeReviewManualRequestResponse,
  CodeReviewPrReviewsResponse,
  RepoClaimMove,
  RepoClaimMoveResponse,
  RepoClaimsResponse,
} from "@yagni/shared";
import type { HarperRunDetail, HarperRunRow } from "../workers/useWorkers.js";

export interface HarperRunsPage {
  runs: HarperRunRow[];
  page: number;
  pageSize: number;
  total: number;
}

export type VerbResult<T = undefined> = { ok: true; value: T } | { ok: false; message: string };

/** What the reviews page asks for: the search, the chips and both sections' pages. */
export interface ReviewsQuery {
  q: string;
  status: string;
  repo: string;
  author: string;
  page: number;
  heldPage: number;
}

export interface ReviewsApi {
  /** `since` is the reader's own midnight, for the header's "reviews today". */
  listReviews(query: ReviewsQuery, since: string): Promise<CodeReviewListResponse>;
  listDeskPrs(deskId: string): Promise<CodeReviewDeskPrsResponse>;
  prReviews(prStateId: string): Promise<CodeReviewPrReviewsResponse | null>;
  rerun(prStateId: string): Promise<VerbResult>;
  requestReview(deskId: string, body: { input: string; repo?: string }): Promise<VerbResult<CodeReviewManualRequestResponse>>;
  repoClaims(): Promise<RepoClaimsResponse>;
  moveRepoClaims(moves: RepoClaimMove[]): Promise<VerbResult<RepoClaimMoveResponse>>;
  archiveWorkItem(workItemId: string): Promise<VerbResult>;
  harperRuns(deskId: string): Promise<HarperRunsPage>;
  harperRun(runId: string): Promise<HarperRunDetail | null>;
}

async function readJson<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || `${what} failed (${res.status})`);
  }
  return (await res.json()) as T;
}

async function verb<T>(path: string, body: unknown, fallback: string): Promise<VerbResult<T>> {
  try {
    const res = await fetch(path, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
    if (!res.ok) return { ok: false, message: payload?.error || `${fallback} (${res.status})` };
    return { ok: true, value: payload as T };
  } catch {
    return { ok: false, message: "Could not reach the server. Try again." };
  }
}

const CR = "/api/code-review";
const enc = encodeURIComponent;

export const reviewsApi: ReviewsApi = {
  async listReviews(query, since) {
    const params = new URLSearchParams();
    if (query.q) params.set("q", query.q);
    if (query.status) params.set("status", query.status);
    if (query.repo) params.set("repo", query.repo);
    if (query.author) params.set("author", query.author);
    if (query.page > 1) params.set("page", String(query.page));
    if (query.heldPage > 1) params.set("heldPage", String(query.heldPage));
    params.set("since", since);
    const res = await fetch(`${CR}/prs?${params.toString()}`, { credentials: "include" });
    return readJson<CodeReviewListResponse>(res, "Reading the pull requests");
  },
  async listDeskPrs(deskId) {
    const res = await fetch(`${CR}/desks/${enc(deskId)}/prs`, { credentials: "include" });
    return readJson<CodeReviewDeskPrsResponse>(res, "Reading the pull requests");
  },
  async prReviews(prStateId) {
    const res = await fetch(`${CR}/pr-state/${enc(prStateId)}/reviews`, { credentials: "include" });
    if (res.status === 404) return null;
    return readJson<CodeReviewPrReviewsResponse>(res, "Reading the review");
  },
  async rerun(prStateId) {
    const result = await verb<unknown>(`${CR}/pr-state/${enc(prStateId)}/rerun`, undefined, "Could not re-run");
    return result.ok ? { ok: true, value: undefined } : result;
  },
  requestReview(deskId, body) {
    return verb<CodeReviewManualRequestResponse>(`${CR}/desks/${enc(deskId)}/review-requests`, body, "Could not ask for a review");
  },
  async repoClaims() {
    const res = await fetch(`${CR}/repo-claims`, { credentials: "include" });
    return readJson<RepoClaimsResponse>(res, "Reading who reviews which repository");
  },
  moveRepoClaims(moves) {
    return verb<RepoClaimMoveResponse>(`${CR}/repo-claims/move`, { moves }, "Could not move the repositories");
  },
  async archiveWorkItem(workItemId) {
    const result = await verb<unknown>(`/api/work-items/${enc(workItemId)}/archive`, {}, "Could not archive");
    return result.ok ? { ok: true, value: undefined } : result;
  },
  async harperRuns(deskId) {
    const res = await fetch(`/api/workers/harper/engagements/${enc(deskId)}/runs`, { credentials: "include" });
    return readJson<HarperRunsPage>(res, "Reading Harper's posts");
  },
  async harperRun(runId) {
    const res = await fetch(`/api/workers/harper/runs/${enc(runId)}`, { credentials: "include" });
    if (res.status === 404) return null;
    return readJson<HarperRunDetail>(res, "Reading the post");
  },
};
